/**
 * The exercise vocabulary (LESSON-TYPES.md).
 *
 * This lives in `core` rather than in `content` because both the builder and the
 * content schemas need it, and two lists of sixteen strings would eventually
 * disagree. `content` imports this one.
 */
export const EXERCISE_TYPES = [
    'listen_choose',
    'audio_word_match',
    'word_bank_order',
    'speak_repeat',
    'speak_answer',
    'match_pairs',
    'picture_choice',
    'build_sentence',
    'meaning_choose',
    'type_answer',
    'fill_blank',
    'dictation',
    'story',
    'sound_focus',
    'order_dialogue',
    'label_scene',
    'gap_text',
];
/**
 * Exercises that cost a child real effort to attempt.
 *
 * Speaking aloud and writing what you hear are the two things a tired
 * nine-year-old will avoid, and two of them back to back is how a lesson stops
 * being finished. The weaving rules use this to keep them apart.
 *
 * `story` is here for a different reason: it is long and demands sustained
 * attention, so it earns the same spacing even though it is not hard.
 */
export const HIGH_EFFORT_TYPES = new Set([
    'speak_repeat',
    'speak_answer',
    'dictation',
    'build_sentence',
    'type_answer',
    'story',
]);
export const isHighEffort = (type) => HIGH_EFFORT_TYPES.has(type);
/**
 * Types safe to open or close a lesson with.
 *
 * The first thirty seconds decide whether a child stays, and the last one decides
 * how they remember the lesson — so both ends are recognition, never production.
 */
export const GENTLE_TYPES = new Set([
    'match_pairs',
    'picture_choice',
    'listen_choose',
    'meaning_choose',
    'audio_word_match',
]);
export const isGentle = (type) => GENTLE_TYPES.has(type);
/**
 * Types where the child says something out loud.
 *
 * They earn a bonus ([Economy](../../docs/ECONOMY.md)) whether or not a
 * microphone heard it: shadow mode pays exactly what mic mode pays, because the
 * mic is optional throughout and a child on a bus must not fall behind one at a
 * desk ([Accessibility](../../docs/ACCESSIBILITY.md)).
 */
export const SPEAKING_TYPES = new Set([
    'speak_repeat',
    'speak_answer',
]);
export const isSpeaking = (type) => SPEAKING_TYPES.has(type);
/**
 * What a child can do instead when the microphone is unavailable or refused.
 *
 * `shadow` means they listen, repeat if they want to, and the app moves on
 * without scoring — full credit. `listen_choose` swaps the exercise for a
 * recognition one. `skip` means there is nothing sensible to do without a mic,
 * so the builder leaves the exercise out entirely.
 *
 * The mic is optional throughout ([Accessibility](../../../docs/ACCESSIBILITY.md)),
 * so every mic exercise must name its way out — enforced by the content linter,
 * not left to whoever authors the file.
 */
export const NON_MIC_FALLBACKS = ['shadow', 'listen_choose', 'skip'];
/**
 * How long one exercise of each type takes a child, in seconds.
 *
 * **These are estimates**, used only for the lesson time budget. They are
 * deliberately generous: a lesson that runs short is fine, one that overruns the
 * fifteen-minute ceiling is a child kept past their attention span. Telemetry
 * replaces them once there is any.
 *
 * An authored exercise may override its own estimate; nothing else may.
 */
export const SECONDS_BY_TYPE = {
    listen_choose: 12,
    audio_word_match: 25,
    word_bank_order: 30,
    speak_repeat: 20,
    speak_answer: 30,
    match_pairs: 30,
    picture_choice: 12,
    build_sentence: 35,
    meaning_choose: 12,
    type_answer: 30,
    fill_blank: 20,
    dictation: 35,
    story: 90,
    sound_focus: 20,
    order_dialogue: 30,
    label_scene: 30,
    // Four to seven gaps in a few sentences: read it, then fill it.
    gap_text: 60,
};
//# sourceMappingURL=exercises.js.map